import { useEffect, useRef, useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { createUserGoogle, setToken, requestOTP, verifyOTP } from '@genfun/core/api/auth';
import { SITE } from '../data/site';
import { Modal } from '../components/Modal';
import { Icon } from '../components/Icon';
import './LoginPopup.css';

const HAS_GOOGLE = !!process.env.REACT_APP_GOOGLE_ID;

// Rendered only under GoogleOAuthProvider (useGoogleLogin throws without it).
function GoogleButton({ onDone, setError }) {
    const ref = localStorage.getItem('referral_code');
    const login = useGoogleLogin({
        onSuccess: async (creds) => {
            const resp = await createUserGoogle(creds, ref);
            if (resp.access_token === undefined && resp.detail !== undefined) {
                setError(resp.detail);
            } else {
                setToken(resp.access_token);
                onDone();
            }
        },
    });

    return (
        <button type="button" onClick={() => login()} className="btn btn--glass btn--lg btn--block">
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/><path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332Z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335"/></svg>
            Continue with Google
        </button>
    );
}

const CODE_LENGTH = 6; // backend: f"{secrets.randbelow(10**6):06d}"
const RESEND_AFTER = 30;

// Six round cells drawn over one real input, so paste and iOS "one-time-code" autofill just work.
function CodeInput({ value, onChange, disabled }) {
    const [focused, setFocused] = useState(false);
    const cells = Array.from({ length: CODE_LENGTH }, (_, i) => value[i] || '');
    const active = Math.min(value.length, CODE_LENGTH - 1);

    return (
        <label className={`otpCells${focused ? ' isFocused' : ''}`}>
            <span className="srOnly">Code from the email</span>
            <input
                className="otpInput"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={CODE_LENGTH}
                autoFocus
                disabled={disabled}
                value={value}
                onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
            />
            {cells.map((c, i) => (
                <span key={i} className={`otpCell${c ? ' isFilled' : ''}${focused && i === active ? ' isActive' : ''}`} aria-hidden="true">{c}</span>
            ))}
        </label>
    );
}

function CodeStep({ email, onBack, onDone }) {
    const [code, setCode] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [wait, setWait] = useState(RESEND_AFTER);
    const ref = localStorage.getItem('referral_code');

    useEffect(() => {
        if (wait <= 0) return undefined;
        const t = setTimeout(() => setWait((w) => w - 1), 1000);
        return () => clearTimeout(t);
    }, [wait]);

    const verify = async (value) => {
        if (value.length < CODE_LENGTH || loading) return;
        setLoading(true);
        setError(null);
        try {
            const [response, status] = await verifyOTP(email, value);
            if (status === 200) { onDone(); return; }
            setError(response?.detail || 'Invalid code');
            setCode('');
        } catch {
            setError('An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const onChange = (value) => {
        setCode(value);
        if (error) setError(null);
        if (value.length === CODE_LENGTH) verify(value); // no need to press the button
    };

    const resend = async () => {
        if (wait > 0 || loading) return;
        setWait(RESEND_AFTER);
        setError(null);
        try {
            const [hasError] = await requestOTP(email, ref);
            if (hasError) setError('Failed to send code');
        } catch {
            setError('An error occurred');
        }
    };

    return (
        <form className="otpStep" onSubmit={(e) => { e.preventDefault(); verify(code); }}>
            <button type="button" className="iconBtn iconBtn--sm otpBack" onClick={onBack} aria-label="Back">
                <Icon name="arrowLeft" size={16} />
            </button>

            <div className="loginHead">
                <span className="loginMark"><Icon name="mail" size={24} /></span>
                <h2 className="modalTitle">Check your email</h2>
                <p className="modalSub">We sent a {CODE_LENGTH}-digit code to</p>
                <p className="otpEmail" title={email}>{email}</p>
            </div>

            <CodeInput value={code} onChange={onChange} disabled={loading} />

            {error && <div className="note note--error">{error}</div>}

            <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={loading || code.length < CODE_LENGTH}>
                {loading ? 'Verifying…' : 'Log in'}
            </button>

            <button type="button" className="loginResend" onClick={resend} disabled={wait > 0}>
                {wait > 0 ? `Resend code in 0:${String(wait).padStart(2, '0')}` : 'Resend code'}
            </button>
        </form>
    );
}

function EmailForm({ onCodeSent }) {
    const emailRef = useRef('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const ref = localStorage.getItem('referral_code');

    const handleRequestOTP = async (e) => {
        e.preventDefault();
        const val = emailRef.current.trim();
        if (!val) { setError('Please enter your email'); return; }
        setLoading(true);
        try {
            const [hasError, status] = await requestOTP(val, ref);
            if (hasError) {
                setError(status === 404 ? 'User not found' : status === 400 ? 'User is registered through Google' : 'Failed to send code');
            } else {
                onCodeSent(val);
            }
        } catch {
            setError('An error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form className="loginForm" onSubmit={handleRequestOTP}>
            <input
                type="email"
                autoComplete="email"
                className="field"
                placeholder="Enter your email"
                onChange={(e) => { emailRef.current = e.target.value; }}
            />
            {error && <div className="note note--error">{error}</div>}
            <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={loading}>
                {loading ? 'Sending…' : 'Continue with email'}
            </button>
        </form>
    );
}

export function LoginPopup({ isOpen, onClose, onSignedIn }) {
    const [error, setError] = useState(null);
    const [codeEmail, setCodeEmail] = useState(null); // set → the "enter the code" screen
    const done = () => { onSignedIn(); onClose(); };

    useEffect(() => { if (!isOpen) { setCodeEmail(null); setError(null); } }, [isOpen]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="sm" mobile="card" label="Log in" className="loginModal">
            {codeEmail ? (
                <CodeStep email={codeEmail} onBack={() => setCodeEmail(null)} onDone={done} />
            ) : (
                <>
                    <div className="loginHead">
                        <span className="loginMark"><Icon name="play" size={22} /></span>
                        <h2 className="modalTitle">Welcome to {SITE.name}</h2>
                        <p className="modalSub">Sign in to start generating</p>
                    </div>

                    {HAS_GOOGLE && (
                        <>
                            <GoogleButton onDone={done} setError={setError} />
                            <div className="loginDivider"><span>or</span></div>
                        </>
                    )}

                    <EmailForm onCodeSent={setCodeEmail} />

                    {error && <div className="note note--error loginError">{error}</div>}
                </>
            )}
        </Modal>
    );
}
